/**
 * File Upload Zone Component
 *
 * Reusable drag-and-drop file upload zone for images and documents
 * Extracted from GuideSignupFlow.tsx mockup
 *
 * Epic: MVP 1.2.1 - Guide Signup Flow
 * Location: src/components/guides/signup/FileUploadZone.jsx
 */

import React, { useState, useRef } from 'react';

/**
 * FileUploadZone Component
 *
 * @param {Object} props
 * @param {Function} props.onFilesSelect - Handler when files are selected (receives File[] array)
 * @param {string} props.error - Error message to display
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.acceptedTypes - Accepted file types (default: images)
 * @param {number} props.maxSize - Max file size in bytes (default: 5MB)
 * @param {boolean} props.multiple - Allow multiple file selection
 * @param {boolean} props.uploading - Whether upload is in progress
 * @param {string} props.label - Label text
 * @param {string} props.description - Description text
 * @param {Array<string>} props.previewUrls - Array of preview URLs to display
 * @param {Array<{id: string, name: string, size: number}>} props.uploadedFiles - Successfully uploaded file records
 * @param {Function} props.onRemoveFile - Handler to remove an uploaded file (receives file id)
 * @returns {JSX.Element}
 */
const FileUploadZone = ({
  onFilesSelect,
  error = '',
  className = '',
  acceptedTypes = 'image/jpeg,image/jpg,image/png,image/webp',
  maxSize = 5 * 1024 * 1024, // 5MB default
  multiple = false,
  uploading = false,
  label = 'Upload Files',
  description = 'Click or drag and drop',
  previewUrls = [],
  uploadedFiles = [],
  onRemoveFile
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState('');
  const fileInputRef = useRef(null);

  /**
   * Validate file
   * @param {File} file - File to validate
   * @returns {boolean} True if valid
   */
  const validateFile = (file) => {
    // Check file type
    const acceptedTypesArray = acceptedTypes.split(',').map((t) => t.trim());
    if (!acceptedTypesArray.includes(file.type)) {
      setValidationError(`File type not supported. Accepted: ${acceptedTypes}`);
      return false;
    }

    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / (1024 * 1024));
      setValidationError(`File too large. Maximum size: ${maxSizeMB}MB`);
      return false;
    }

    return true;
  };

  /**
   * Handle file selection
   * @param {FileList} fileList - Selected files
   */
  const handleFileSelect = (fileList) => {
    if (!fileList || fileList.length === 0) {return;}

    setValidationError('');

    const filesArray = Array.from(fileList);
    const validFiles = filesArray.filter((file) => validateFile(file));

    if (validFiles.length > 0 && onFilesSelect) {
      onFilesSelect(validFiles);
    }
  };

  /**
   * Handle file input change
   * @param {Event} e - Change event
   */
  const handleInputChange = (  _e) => {
    handleFileSelect(_e.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle drag over
   * @param {Event} e - Drag event
   */
  const handleDragOver = (  _e) => {
    _e.preventDefault();
    setIsDragging(true);
  };

  /**
   * Handle drag leave
   * @param {Event} e - Drag event
   */
  const handleDragLeave = (  _e) => {
    _e.preventDefault();
    setIsDragging(false);
  };

  /**
   * Handle drop
   * @param {Event} e - Drop event
   */
  const handleDrop = (  _e) => {
    _e.preventDefault();
    setIsDragging(false);
    handleFileSelect(_e.dataTransfer.files);
  };

  /**
   * Trigger file input click
   */
  const handleClick = () => {
    if (!uploading && fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  /**
   * Get file type icon
   * @param {string} type - File type
   * @returns {JSX.Element}
   */
  const getFileIcon = (type) => {
    if (type.startsWith('image/')) {
      return (
        <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 48 48">
          <path
            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      );
    }

    return (
      <svg className="h-8 w-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
          clipRule="evenodd"
        />
      </svg>
    );
  };

  return (
    <div className={`${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes}
        multiple={multiple}
        onChange={handleInputChange}
        className="hidden"
        disabled={uploading}
      />

      {/* Drag-and-drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        className={`
          relative p-8 rounded-lg border-2 border-dashed cursor-pointer
          transition-all duration-200
          ${isDragging ? 'border-emerald-500 bg-emerald-50' : 'border-gray-300 bg-white'}
          ${uploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-emerald-400 hover:bg-emerald-50'}
          ${error || validationError ? 'border-red-500 bg-red-50' : ''}
        `}
      >
        <div className="text-center">
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-3"></div>
              <p className="text-sm text-gray-600 font-medium">Uploading...</p>
              <p className="text-xs text-gray-500 mt-1">Please wait</p>
            </>
          ) : (
            <>
              {getFileIcon(acceptedTypes.split(',')[0])}
              <p className="mt-3 text-sm text-gray-700 font-medium">
                {description}
              </p>
              <p className="mt-1 text-xs text-gray-500">
                {acceptedTypes.includes('pdf') || acceptedTypes.includes('application/pdf')
                  ? 'PDF, JPG, or PNG'
                  : acceptedTypes.includes('image')
                    ? 'JPG, PNG, or WEBP'
                    : 'Supported file types'}
                {' · '}
                Max {Math.round(maxSize / (1024 * 1024))}MB
              </p>
              {multiple && (
                <p className="mt-1 text-xs text-gray-400">
                  You can upload multiple files at once
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Preview Grid */}
      {previewUrls.length > 0 && (
        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
          {previewUrls.map((url, index) => (
            <div
              key={index}
              className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 bg-gray-50"
            >
              <img
                src={url}
                alt={`Preview ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Files List */}
      {uploadedFiles.length > 0 && (
        <ul className="mt-3 space-y-2">
          {uploadedFiles.map((file) => (
            <li
              key={file.id}
              className="flex items-center justify-between px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md"
            >
              <div className="flex items-center min-w-0">
                <svg className="w-4 h-4 text-emerald-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-gray-700 truncate">{file.name}</span>
                {file.size !== null && file.size !== undefined && (
                  <span className="ml-2 text-xs text-gray-400 flex-shrink-0">
                    {Math.round(file.size / 1024)}KB
                  </span>
                )}
              </div>
              {onRemoveFile && (
                <button
                  type="button"
                  onClick={() => onRemoveFile(file.id)}
                  className="ml-3 flex-shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                  aria-label={`Remove ${file.name}`}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Error Messages */}
      {(error || validationError) && (
        <p className="mt-2 text-sm text-red-600 flex items-start">
          <svg
            className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          {error || validationError}
        </p>
      )}
    </div>
  );
};

export default FileUploadZone;
